/**
 * OnboardingModal - 3-step modal shown after signup.
 * Steps: Exam date → Study goal → Prior experience.
 * Skippable - but a banner on Dashboard nudges users to complete.
 */
import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar, Clock, GraduationCap, Sparkles, X, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const GOAL_OPTIONS = [
  { value: 30, label: "30 min/day", sub: "Light prep, casual pace" },
  { value: 60, label: "1 hr/day", sub: "Steady ADC prep" },
  { value: 120, label: "2 hrs/day", sub: "Serious cram mode" },
  { value: 180, label: "3+ hrs/day", sub: "Full-time exam prep" },
];

const PRIOR_OPTIONS = [
  { value: "first", label: "First attempt", sub: "I'm sitting it for the first time" },
  { value: "failed_before", label: "Resitting", sub: "I've sat it before and want to pass this time" },
  { value: "exploring", label: "Just exploring", sub: "I'm researching the exam" },
];

const EXAM_OPTIONS = [
  { value: "march_2027", label: "March 2027 (17–18 Mar)" },
  { value: "september_2027", label: "September 2027" },
  { value: "not_sure", label: "Not sure yet" },
];

const OnboardingModal = ({ onClose }) => {
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [examTarget, setExamTarget] = useState(null);
  const [goalMinutes, setGoalMinutes] = useState(60);
  const [prior, setPrior] = useState("first");
  const [university, setUniversity] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (skip = false) => {
    setSaving(true);
    try {
      const payload = skip ? {} : {
        exam_target: examTarget,
        exam_date: examTarget === "march_2027" ? new Date("2027-03-17T00:00:00Z").toISOString() : null,
        study_goal_minutes: goalMinutes,
        prior_attempts: prior,
        university: university.trim() || null,
      };
      await axios.post(`${BACKEND_URL}/api/auth/onboarding`, payload);
      await refresh();
      if (!skip) toast.success("All set! Your dashboard is now personalised.");
      onClose();
    } catch (e) {
      toast.error("Could not save onboarding - you can complete it later from your profile.");
    } finally { setSaving(false); }
  };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => s - 1);

  return (
    <div data-testid="onboarding-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header w/ progress */}
        <div className="px-6 pt-6 pb-2 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-primary" />
          <p className="text-sm font-semibold flex-1">Personalise your study (30 sec)</p>
          <button data-testid="onboarding-skip" onClick={() => submit(true)} disabled={saving} className="text-xs text-muted-foreground hover:text-foreground">Skip</button>
        </div>
        <div className="px-6 mb-4">
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-secondary"}`} />
            ))}
          </div>
        </div>

        <div className="px-6 pb-6">
          {/* STEP 1 - Exam date */}
          {step === 0 && (
            <div data-testid="onboarding-step-1">
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-3">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-1">When is your ADC Part 1 exam?</h3>
              <p className="text-sm text-muted-foreground mb-5">We&apos;ll size your study plan to the time you have left.</p>
              <div className="space-y-3">
                {EXAM_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`onboarding-exam-${opt.value}`}
                    onClick={() => setExamTarget(opt.value)}
                    className={`w-full text-left p-4 rounded-xl border text-base font-medium transition ${
                      examTarget === opt.value
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  data-testid="onboarding-exam-skip"
                  onClick={next}
                  className="px-4 py-3 rounded-xl border border-border font-medium text-sm"
                >
                  Skip for now
                </button>
                <button
                  data-testid="onboarding-next-1"
                  onClick={next}
                  className="flex-1 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium flex items-center justify-center gap-2"
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 - Study goal */}
          {step === 1 && (
            <div data-testid="onboarding-step-2">
              <div className="w-12 h-12 rounded-xl bg-warning/15 flex items-center justify-center mb-3">
                <Clock className="w-6 h-6 text-warning" />
              </div>
              <h3 className="text-xl font-bold mb-1">How much can you study per day?</h3>
              <p className="text-sm text-muted-foreground mb-5">Be realistic - we&apos;ll plan around your real life.</p>
              <div className="space-y-2">
                {GOAL_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`onboarding-goal-${opt.value}`}
                    onClick={() => setGoalMinutes(opt.value)}
                    className={`w-full text-left p-3 rounded-xl border transition ${
                      goalMinutes === opt.value
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <p className="font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.sub}</p>
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={back} className="px-4 py-3 rounded-xl border border-border font-medium">Back</button>
                <button data-testid="onboarding-next-2" onClick={next} className="flex-1 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium flex items-center justify-center gap-2">
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 - Prior experience + university */}
          {step === 2 && (
            <div data-testid="onboarding-step-3">
              <div className="w-12 h-12 rounded-xl bg-success/15 flex items-center justify-center mb-3">
                <GraduationCap className="w-6 h-6 text-success" />
              </div>
              <h3 className="text-xl font-bold mb-1">Is this your first ADC attempt?</h3>
              <p className="text-sm text-muted-foreground mb-5">This helps us tailor your study plan and tutor recommendations.</p>
              <div className="space-y-2 mb-4">
                {PRIOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`onboarding-prior-${opt.value}`}
                    onClick={() => setPrior(opt.value)}
                    className={`w-full text-left p-3 rounded-xl border transition ${
                      prior === opt.value
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <p className="font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.sub}</p>
                  </button>
                ))}
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Where did you study dentistry? (optional)</label>
                <input
                  data-testid="onboarding-university"
                  type="text"
                  value={university}
                  onChange={(e) => setUniversity(e.target.value)}
                  placeholder="e.g. University of Karachi"
                  className="w-full mt-1 px-3 py-3 rounded-xl bg-background border border-border text-base"
                />
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={back} className="px-4 py-3 rounded-xl border border-border font-medium">Back</button>
                <button
                  data-testid="onboarding-finish"
                  onClick={() => submit(false)}
                  disabled={saving}
                  className="flex-1 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Personalise my dashboard"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
